import { type QueryClient, QueryObserverOptions } from "@tanstack/react-query";
import { ModelName, QueryMethodForModel, ParamsForMethod, QueryDataForMethod } from "../../types";
import { buildTellescopeQueryKey } from "../../keys";

/**
 * Arguments for setting query defaults.
 *
 * @template Model - The Tellescope model name (must support query methods)
 * @template Method - The query method for the model
 */
export interface SetTellescopeQueryDefaultsArgs<
  Model extends ModelName,
  Method extends QueryMethodForModel<Model>,
  TError = Error,
  TData = QueryDataForMethod<Model, Method>,
> {
  /** The Tellescope model name */
  model: Model;
  /** Query method name */
  method: Method;
  /** Optional request parameters to narrow down which queries to set defaults for. If omitted, sets defaults for all queries for the method. */
  request?: Partial<ParamsForMethod<Model, Method>>;
  /** The default query options to set */
  defaults: Partial<
    Omit<
      QueryObserverOptions<QueryDataForMethod<Model, Method>, TError, TData, QueryDataForMethod<Model, Method>>,
      "queryKey"
    >
  >;
  /** The TanStack Query client instance */
  queryClient: QueryClient;
}

/**
 * Sets default options for matching Tellescope queries.
 * These defaults will be applied to all queries that match the specified key.
 * Query defaults are useful for setting default configurations like staleTime, gcTime, etc.
 *
 * @param args - The set query defaults arguments
 *
 * @example
 * ```tsx
 * // Set defaults for all getOne queries
 * setTellescopeQueryDefaults({
 *   queryClient,
 *   model: "users",
 *   method: "getOne",
 *   defaults: {
 *     staleTime: 5000,
 *     gcTime: 10000,
 *   },
 * });
 *
 * // Set defaults for a specific query
 * setTellescopeQueryDefaults({
 *   queryClient,
 *   model: "users",
 *   method: "getOne",
 *   request: { id: "user-123" },
 *   defaults: {
 *     staleTime: 15000,
 *   },
 * });
 *
 * // Set multiple query options
 * setTellescopeQueryDefaults({
 *   queryClient,
 *   model: "users",
 *   method: "getSome",
 *   defaults: {
 *     staleTime: 10000,
 *     gcTime: 20000,
 *     refetchOnMount: false,
 *     refetchOnWindowFocus: true,
 *   },
 * });
 * ```
 */
export const setTellescopeQueryDefaults = <
  Model extends ModelName,
  Method extends QueryMethodForModel<Model>,
  TError = Error,
  TData = QueryDataForMethod<Model, Method>,
>({
  model,
  method,
  request,
  defaults,
  queryClient,
}: SetTellescopeQueryDefaultsArgs<Model, Method, TError, TData>) => {
  const queryKey = buildTellescopeQueryKey({ model, method, request });

  queryClient.setQueryDefaults(queryKey, defaults);
};
